import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { SimpleDropdown } from "./ui/simple-dropdown";
import ChakraDatePicker from "./ui/chakra-datepicker";
import {
  X,
  Save,
  User,
  Calendar,
  Users,
  Hash,
  CreditCard,
  GraduationCap,
  CheckCircle,
  Search,
  Trash2,
  DollarSign,
  UserPlus,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { educationAPI } from "../api/axiosClient";
import ParentCreateForm from "./ParentCreateForm";

interface StudentCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  schoolId: string;
}

const StudentCreateForm: React.FC<StudentCreateFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  schoolId,
}) => {
  // Function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    gender: 1, // Male
    studentNumber: "",
    enrollmentDate: getTodayDate(), // Set today's date as default
    identityNumber: "",
    status: 1, // Active
    classroomId: null as string | null, 
    parentIds: [] as string[],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Classroom state
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState<boolean>(false);

  // Parent management state
  interface SelectedParent {
    id: string;
    fullName: string;
    phone: string;
    relationType: number;
    isPaymentManager: boolean;
  }
  const [selectedParents, setSelectedParents] = useState<SelectedParent[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [parentSearchTerm, setParentSearchTerm] = useState<string>("");
  const [showParentDropdown, setShowParentDropdown] = useState<boolean>(false);
  const [showParentCreateForm, setShowParentCreateForm] =
    useState<boolean>(false);

  // Load classrooms when form opens
  useEffect(() => {
    if (isOpen) {
      loadClassrooms();
    }
  }, [isOpen]);

  // Load classrooms function
  const loadClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const response = await educationAPI.getClassrooms({
        Q: "",
        Page: 1,
        Size: 1000, // Get all classrooms
        OrderBy: 0,
        SortBy: "",
      });

      if (response.success && response.data?.items) {
        setClassrooms(response.data.items);
      } else {
        console.error("Failed to load classrooms:", response.message);
        toast.error("Sınıflar yüklenirken hata oluştu");
      }
    } catch (error) {
      console.error("Error loading classrooms:", error);
      toast.error("Sınıflar yüklenirken hata oluştu");
    } finally {
      setLoadingClassrooms(false);
    }
  };

  // Search parents function - API based
  const searchParents = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await educationAPI.listParents({
        Q: searchTerm,
        Page: 1,
        Size: 10,
        OrderBy: 0,
      });

      if (response.success && response.data?.items) {
        // Filter out already selected parents
        const selectedIds = selectedParents.map((p) => p.id);
        const availableParents = response.data.items.filter(
          (parent: any) => !selectedIds.includes(parent.id),
        );
        setSearchResults(availableParents);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching parents:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (parentSearchTerm.trim()) {
        searchParents(parentSearchTerm);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [parentSearchTerm, selectedParents]);

  // Add parent to selected list
  const handleAddParent = (parent: any) => {
    if (selectedParents.find((p) => p.id === parent.id)) {
      toast.error("Bu veli zaten eklenmiş");
      return;
    }
    setSelectedParents([
      ...selectedParents,
      {
        id: parent.id,
        fullName: parent.fullName,
        phone:
          parent.phoneCode && parent.phoneNumber
            ? `+${parent.phoneCode} ${parent.phoneNumber}`
            : "",
        relationType: 1, // Default: Anne
        isPaymentManager: selectedParents.length === 0, // First parent is payment manager by default
      },
    ]);
    setParentSearchTerm("");
    setShowParentDropdown(false);
  };

  // Remove parent from selected list
  const handleRemoveParent = (parentId: string) => {
    const updatedParents = selectedParents.filter((p) => p.id !== parentId);
    // If removed parent was payment manager, assign to first remaining parent
    if (
      updatedParents.length > 0 &&
      !updatedParents.some((p) => p.isPaymentManager)
    ) {
      updatedParents[0].isPaymentManager = true;
    }
    setSelectedParents(updatedParents);
  };

  // Update parent relation type
  const handleUpdateRelationType = (parentId: string, relationType: number) => {
    setSelectedParents(
      selectedParents.map((p) =>
        p.id === parentId ? { ...p, relationType } : p,
      ),
    );
  };

  // Set payment manager
  const handleSetPaymentManager = (parentId: string) => {
    setSelectedParents(
      selectedParents.map((p) => ({
        ...p,
        isPaymentManager: p.id === parentId,
      })),
    );
  };

  // TC Kimlik No validation function
  const validateTCKimlikNo = (tcNo: string): boolean => {
    if (tcNo.length !== 11) return false;

    const digits = tcNo.split("").map(Number);

    // First digit cannot be 0
    if (digits[0] === 0) return false;

    // Calculate checksum
    const sum1 = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
    const sum2 = digits[1] + digits[3] + digits[5] + digits[7];

    const check1 = (sum1 * 7 - sum2) % 10;
    const check2 = (sum1 + sum2 + check1) % 10;

    return check1 === digits[9] && check2 === digits[10];
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = "Ad alanı zorunludur";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Soyad alanı zorunludur";
    }

    if (!formData.birthDate?.trim()) {
      newErrors.birthDate = "Doğum tarihi alanı zorunludur";
    }

    if (!formData.studentNumber?.trim()) {
      newErrors.studentNumber = "Öğrenci numarası alanı zorunludur";
    }

    if (!formData.identityNumber?.trim()) {
      newErrors.identityNumber = "Kimlik numarası alanı zorunludur";
    } else if (!validateTCKimlikNo(formData.identityNumber)) {
      newErrors.identityNumber = "Geçersiz TC kimlik numarası";
    }

    // Veli zorunluluğu kontrolü
    if (selectedParents.length === 0) {
      newErrors.parents = "En az bir veli eklemelisiniz";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Build v2 request format with parents included
      const request = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate
          ? new Date(formData.birthDate).toISOString()
          : "",
        gender: formData.gender,
        studentNumber: formData.studentNumber,
        enrollmentDate: formData.enrollmentDate
          ? new Date(formData.enrollmentDate).toISOString()
          : "",
        identityNumber: formData.identityNumber,
        status: formData.status,
        schoolId: schoolId,
        classroomId: formData.classroomId ?? null, 
        parents: selectedParents.map((parent) => ({
          parentId: parent.id,
          parentRelationType: parent.relationType,
          isPaymentManager: parent.isPaymentManager,
        })),
      };

      const response = await educationAPI.createStudent(request);

      if (response.success) {
        toast.success("Öğrenci başarıyla eklendi");
        onSuccess();
        onClose();
        resetForm();
      } else {
        toast.error(response.message || "Öğrenci eklenirken hata oluştu");
      }
    } catch (error: any) {
      console.error("Error creating student:", error);
      toast.error("Öğrenci eklenirken hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      birthDate: "",
      gender: 1,
      studentNumber: "",
      enrollmentDate: getTodayDate(), // Set today's date as default
      identityNumber: "",
      status: 1,
      classroomId: null, 
      parentIds: [],
    });
    setErrors({});
    setSelectedParents([]);
    setParentSearchTerm("");
    setSearchResults([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">
              Yeni Öğrenci Ekle
            </CardTitle>
            <CardDescription>Öğrenci bilgilerini girin</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ad */}
              <div>
                <Label
                  htmlFor="firstName"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Ad <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={errors.firstName ? "border-red-500" : ""}
                  placeholder="Adını girin"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Soyad */}
              <div>
                <Label
                  htmlFor="lastName"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Soyad <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={errors.lastName ? "border-red-500" : ""}
                  placeholder="Soyadını girin"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* Doğum Tarihi */}
              <div className="event-endsAt-fix">
                <Label
                  htmlFor="birthDate"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Doğum Tarihi <span className="text-red-500 ml-1">*</span>
                </Label>
                <ChakraDatePicker
                  value={formData.birthDate}
                  onChange={(value) => handleInputChange("birthDate", value)}
                  placeholder="Doğum tarihi seçin"
                  hasError={!!errors.birthDate}
                  maxDate={new Date()}
                />
                {errors.birthDate && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.birthDate}
                  </p>
                )}
                <style>{`
                        .event-endsAt-fix .react-datepicker__close-icon {
                          right: 34px;
                          top: 50%;
                          transform: translateY(-50%);
                          height: 100%;
                          display: flex;
                          align-items: center;
                        }
                        .event-endsAt-fix .react-datepicker__close-icon::after {
                          position: static;
                          transform: none;
                          margin: 0;
                        }
                      `}</style>
              </div>

              {/* Cinsiyet */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Cinsiyet <span className="text-red-500 ml-1">*</span>
                </Label>
                <SimpleDropdown
                  options={[
                    { id: "1", label: "Erkek", value: 1 },
                    { id: "2", label: "Kadın", value: 2 },
                  ]}
                  value={
                    formData.gender
                      ? {
                          id: String(formData.gender),
                          label: formData.gender === 1 ? "Erkek" : "Kadın",
                          value: formData.gender,
                        }
                      : null
                  }
                  onChange={(option) => {
                    if (option) {
                      handleInputChange("gender", option.value);
                    }
                  }}
                  placeholder="Cinsiyet seçiniz"
                  className="w-full"
                />
              </div>

              {/* Öğrenci Numarası */}
              <div>
                <Label
                  htmlFor="studentNumber"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <Hash className="w-4 h-4 mr-2" />
                  Öğrenci Numarası <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="studentNumber"
                  type="text"
                  value={formData.studentNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // Sadece rakam
                    handleInputChange("studentNumber", value);
                  }}
                  className={errors.studentNumber ? "border-red-500" : ""}
                  placeholder="Öğrenci numarasını girin (sadece rakam)"
                />
                {errors.studentNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.studentNumber}
                  </p>
                )}
              </div>

              {/* Kimlik Numarası */}
              <div>
                <Label
                  htmlFor="identityNumber"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Kimlik Numarası <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="identityNumber"
                  type="text"
                  maxLength={11}
                  value={formData.identityNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // Sadece rakam
                    handleInputChange("identityNumber", value);

                    // Validation
                    if (value.length === 11) {
                      if (validateTCKimlikNo(value)) {
                        setErrors((prev) => ({ ...prev, identityNumber: "" }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          identityNumber: "Geçersiz TC kimlik numarası",
                        }));
                      }
                    } else if (value.length > 0) {
                      setErrors((prev) => ({
                        ...prev,
                        identityNumber:
                          "TC kimlik numarası 11 haneli olmalıdır",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, identityNumber: "" }));
                    }
                  }}
                  className={`${errors.identityNumber ? "border-red-500" : ""}`}
                  placeholder="TC kimlik numarasını girin (11 haneli)"
                />
                {errors.identityNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.identityNumber}
                  </p>
                )}
              </div>

              {/* Kayıt Tarihi */}
              <div className="event-endsAt-fix">
                <Label
                  htmlFor="enrollmentDate"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Kayıt Tarihi
                </Label>
                <ChakraDatePicker
                  value={formData.enrollmentDate}
                  onChange={(value) =>
                    handleInputChange("enrollmentDate", value)
                  }
                  placeholder="Kayıt tarihi seçin"
                />
                <style>{`
                        .event-endsAt-fix .react-datepicker__close-icon {
                          right: 34px;
                          top: 50%;
                          transform: translateY(-50%);
                          height: 100%;
                          display: flex;
                          align-items: center;
                        }
                        .event-endsAt-fix .react-datepicker__close-icon::after {
                          position: static;
                          transform: none;
                          margin: 0;
                        }
                      `}</style>
              </div>

              {/* Sınıf */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Sınıf
                </Label>
                <SimpleDropdown
                  options={classrooms.map((classroom) => ({
                    id: classroom.id,
                    label: classroom.name,
                    value: classroom.id,
                  }))}
                  value={
                    formData.classroomId
                      ? {
                          id: formData.classroomId,
                          label:
                            classrooms.find(
                              (c) => c.id === formData.classroomId,
                            )?.name || "Sınıf",
                          value: formData.classroomId,
                        }
                      : null
                  }
                  onChange={(option) => {
                    if (option) {
                      handleInputChange("classroomId", option.value);
                    } else {
                      handleInputChange("classroomId", null);
                    }
                  }}
                  placeholder={
                    loadingClassrooms ? "Sınıflar yükleniyor..." : "Sınıf seçin"
                  }
                  className="w-full"
                  disabled={loadingClassrooms}
                />
              </div>

              {/* Durum */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Durum <span className="text-red-500 ml-1">*</span>
                </Label>
                <SimpleDropdown
                  options={[
                    { id: "1", label: "Aktif", value: 1 },
                    { id: "2", label: "Mezun", value: 2 },
                    { id: "3", label: "Nakil", value: 3 },
                    { id: "4", label: "Askıya Alındı", value: 4 },
                    { id: "5", label: "Ayrıldı", value: 5 },
                  ]}
                  value={
                    formData.status
                      ? {
                          id: String(formData.status),
                          label:
                            formData.status === 1
                              ? "Aktif"
                              : formData.status === 2
                                ? "Mezun"
                                : formData.status === 3
                                  ? "Nakil"
                                  : formData.status === 4
                                    ? "Askıya Alındı"
                                    : formData.status === 5
                                      ? "Ayrıldı"
                                      : "Aktif",
                          value: formData.status,
                        }
                      : null
                  }
                  onChange={(option) => {
                    if (option) {
                      handleInputChange("status", option.value);
                    }
                  }}
                  placeholder="Durum seçiniz"
                  className="w-full"
                />
              </div>
            </div>

            {/* Veli Yönetimi Bölümü */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <Label
                  className={`text-sm font-medium mb-0 flex items-center ${errors.parents ? "text-red-600" : "text-gray-700"}`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Veli Yönetimi <span className="text-red-500 ml-1">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[#9B51E0] text-[#9B51E0] hover:bg-[#9B51E0] hover:text-white"
                  onClick={() => setShowParentCreateForm(true)}
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Yeni Veli Ekle
                </Button>
              </div>
              {errors.parents && (
                <p className="text-sm text-red-600 mb-3">{errors.parents}</p>
              )}

              {/* Veli Arama */}
              <div className="relative mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    value={parentSearchTerm}
                    onChange={(e) => {
                      setParentSearchTerm(e.target.value);
                      setShowParentDropdown(true);
                    }}
                    onFocus={() => setShowParentDropdown(true)}
                    placeholder={
                      isSearching
                        ? "Aranıyor..."
                        : "Veli ara (isim veya telefon)..."
                    }
                    className="pl-10"
                  />
                </div>

                {/* Dropdown */}
                {showParentDropdown &&
                  parentSearchTerm &&
                  searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchResults.map((parent) => (
                        <button
                          key={parent.id}
                          type="button"
                          onClick={() => handleAddParent(parent)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                        >
                          <span className="font-medium">{parent.fullName}</span>
                          <span className="text-sm text-gray-500">
                            {parent.phoneCode && parent.phoneNumber
                              ? `+${parent.phoneCode} ${parent.phoneNumber}`
                              : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                {showParentDropdown &&
                  parentSearchTerm &&
                  !isSearching &&
                  searchResults.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                      <p className="text-center text-gray-500 mb-3">
                        Veli bulunamadı
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full border-[#9B51E0] text-[#9B51E0] hover:bg-[#9B51E0] hover:text-white"
                        onClick={() => {
                          setShowParentDropdown(false);
                          setShowParentCreateForm(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Yeni Veli Ekle
                      </Button>
                    </div>
                  )}

                {showParentDropdown && parentSearchTerm && isSearching && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#9B51E0] mr-2"></div>
                      Aranıyor...
                    </div>
                  </div>
                )}
              </div>

              {/* Seçili Veliler Listesi */}
              {selectedParents.length > 0 && (
                <div className="space-y-3">
                  {selectedParents.map((parent) => (
                    <div
                      key={parent.id}
                      className={`p-4 rounded-lg border ${
                        parent.isPaymentManager
                          ? "border-green-300 bg-green-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">{parent.fullName}</span>
                          {parent.phone && (
                            <span className="text-sm text-gray-500">
                              ({parent.phone})
                            </span>
                          )}
                          {parent.isPaymentManager && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ₺ Ödeme Yöneticisi
                            </span>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveParent(parent.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Yakınlık Türü */}
                        <div className="flex-1">
                          <SimpleDropdown
                            options={[
                              { id: "1", label: "Anne", value: 1 },
                              { id: "2", label: "Baba", value: 2 },
                              { id: "3", label: "Vasi", value: 3 },
                              { id: "4", label: "Diğer", value: 4 },
                            ]}
                            value={{
                              id: String(parent.relationType),
                              label:
                                parent.relationType === 1
                                  ? "Anne"
                                  : parent.relationType === 2
                                    ? "Baba"
                                    : parent.relationType === 3
                                      ? "Vasi"
                                      : "Diğer",
                              value: parent.relationType,
                            }}
                            onChange={(option) => {
                              if (option) {
                                handleUpdateRelationType(
                                  parent.id,
                                  option.value as number,
                                );
                              }
                            }}
                            placeholder="Yakınlık seçin"
                            className="w-full"
                          />
                        </div>

                        {/* Ödeme Yöneticisi Yap Butonu */}
                        {!parent.isPaymentManager && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetPaymentManager(parent.id)}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            ₺ Ödeme Yöneticisi Yap
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedParents.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Henüz veli eklenmedi. Yukarıdaki arama kutusunu kullanarak
                  veli ekleyebilirsiniz.
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-8 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#9B51E0] hover:bg-[#8B4AD9] text-white"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ekleniyor...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Parent Create Form Modal */}
      <ParentCreateForm
        isOpen={showParentCreateForm}
        onClose={() => setShowParentCreateForm(false)}
        onSuccess={() => {
          setShowParentCreateForm(false);
        }}
        onParentCreated={(newParent) => {
          setSelectedParents((prev) => [
            ...prev,
            {
              id: newParent.id,
              fullName: newParent.fullName,
              phone:
                newParent.phoneCode && newParent.phoneNumber
                  ? `+${newParent.phoneCode} ${newParent.phoneNumber}`
                  : "",
              relationType: 1,
              isPaymentManager: prev.length === 0,
            },
          ]);
          if (errors.parents) {
            setErrors((prev) => ({ ...prev, parents: "" }));
          }
          setParentSearchTerm("");
          setShowParentDropdown(false);
          toast.success("Veli eklendi ve öğrenciye atandı");
        }}
      />
    </div>
  );
};

export default StudentCreateForm;
